import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useFamilyContext } from '../context/FamilyContext';
import AssetModal from '../components/AssetModal';
import { format, isPast, addMonths } from 'date-fns';

const { FiPlus, FiBox, FiSearch, FiTruck, FiAlertCircle, FiTrash2, FiEdit } = FiIcons;

export default function Assets() {
  const { state, dispatch } = useFamilyContext();
  const { assets } = state;
  const [showModal, setShowModal] = useState(false);
  const [editingAsset, setEditingAsset] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const filteredAssets = (assets || []).filter(asset => 
    asset.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    asset.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalValue = filteredAssets.reduce((sum, asset) => sum + (Number(asset.price) || 0), 0);

  const handleSave = (assetData) => {
    if (editingAsset) {
      dispatch({ type: 'UPDATE_ASSET', payload: { ...editingAsset, ...assetData } });
      setEditingAsset(null);
    } else {
      dispatch({ type: 'ADD_ASSET', payload: assetData });
    }
  };

  const handleDelete = (id) => {
    if (confirm('Möchten Sie dieses Asset wirklich löschen?')) {
      dispatch({ type: 'DELETE_ASSET', payload: id });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Inventar & Assets</h1>
          <p className="text-gray-600 mt-2">Verwalten Sie Wertsachen, Geräte und Hausrat</p>
        </div>
        <button
          onClick={() => { setEditingAsset(null); setShowModal(true); }}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 mt-4 sm:mt-0"
        >
          <SafeIcon icon={FiPlus} className="w-4 h-4 mr-2" />
          Neues Asset
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <p className="text-sm text-gray-500">Gesamtwert</p>
          <p className="text-2xl font-bold text-gray-900">€ {totalValue.toFixed(2)}</p>
        </div>
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <p className="text-sm text-gray-500">Anzahl Assets</p>
          <p className="text-2xl font-bold text-gray-900">{filteredAssets.length}</p>
        </div>
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 flex items-center">
         <SafeIcon icon={FiSearch} className="w-5 h-5 text-gray-400 mr-3" />
         <input 
            type="text" 
            placeholder="Suchen..." 
            className="w-full outline-none text-gray-700"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
         />
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 font-medium text-gray-500">Name</th>
                <th className="px-6 py-3 font-medium text-gray-500">Kategorie</th>
                <th className="px-6 py-3 font-medium text-gray-500">Kaufdatum</th>
                <th className="px-6 py-3 font-medium text-gray-500">Garantie</th>
                <th className="px-6 py-3 font-medium text-gray-500">Wert</th>
                <th className="px-6 py-3 font-medium text-gray-500 text-right">Aktionen</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              <AnimatePresence>
                {filteredAssets.map(asset => {
                    const warrantyExpired = asset.warrantyEnd && isPast(new Date(asset.warrantyEnd));
                    const warrantyNear = asset.warrantyEnd && !warrantyExpired && isPast(addMonths(new Date(asset.warrantyEnd), -1));
                    
                    return (
                    <motion.tr 
                        key={asset.id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="hover:bg-gray-50"
                    >
                        <td className="px-6 py-4 font-medium text-gray-900">{asset.name}</td>
                        <td className="px-6 py-4 text-gray-600">
                            <span className="px-2 py-1 bg-gray-100 rounded-full text-xs">
                                {asset.category}
                            </span>
                        </td>
                        <td className="px-6 py-4 text-gray-600">{asset.purchaseDate ? format(new Date(asset.purchaseDate), 'dd.MM.yyyy') : '-'}</td>
                        <td className="px-6 py-4">
                            {asset.warrantyEnd ? (
                                <div className="flex items-center space-x-1">
                                    <span className={`${warrantyExpired ? 'text-red-600' : warrantyNear ? 'text-yellow-600' : 'text-green-600'}`}>
                                        {format(new Date(asset.warrantyEnd), 'dd.MM.yyyy')}
                                    </span>
                                    {warrantyExpired && <SafeIcon icon={FiAlertCircle} className="w-4 h-4 text-red-500" title="Abgelaufen" />}
                                </div>
                            ) : '-'}
                        </td>
                        <td className="px-6 py-4 text-gray-900 font-medium">
                            {asset.price ? `€ ${Number(asset.price).toFixed(2)}` : '-'}
                        </td>
                        <td className="px-6 py-4 text-right space-x-2">
                            <button onClick={() => { setEditingAsset(asset); setShowModal(true); }} className="text-blue-600 hover:text-blue-800">
                                <SafeIcon icon={FiEdit} className="w-4 h-4" />
                            </button>
                            <button onClick={() => handleDelete(asset.id)} className="text-red-600 hover:text-red-800">
                                <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                            </button>
                        </td>
                    </motion.tr>
                    );
                })}
              </AnimatePresence>
              {filteredAssets.length === 0 && (
                <tr>
                    <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                        Keine Assets gefunden.
                    </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <AssetModal
          asset={editingAsset}
          onClose={() => setShowModal(false)}
          onSave={handleSave}
        />
      )}
    </div>
  );
}